import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Bell, User } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';

import { selectCurrentProject, selectInstanceInfo } from '@/entities/layout';
import { toggleMobileSidebar } from '@/entities/navigation';

import { HeaderSearch, FilterTrigger } from '@/features/header-filters';
import { useProjectSiteParams } from '@/shared/lib';
import type { AppDispatch } from '@/app/types/store';

export const MobileHeader = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const { projectId, siteId } = useProjectSiteParams();
  const location = useLocation();

  const instanceInfoSelector = useMemo(
    () => (projectId && siteId ? selectInstanceInfo(projectId, siteId) : () => null),
    [projectId, siteId]
  );
  const currentProjectSelector = useMemo(
    () => (projectId && siteId ? selectCurrentProject(projectId, siteId) : () => null),
    [projectId, siteId]
  );

  const currentProject = useSelector(currentProjectSelector);
  const instanceInfo = useSelector(instanceInfoSelector);

  const isDashboard = location.pathname.includes('/dashboard');

  const handleLogoClick = () => {
    dispatch(toggleMobileSidebar());
  };

  return (
    <header className="flex flex-col gap-4 px-5 py-3 border-b bg-background lg:hidden">
      <div className="flex items-center justify-between gap-2">
        <button
          onClick={handleLogoClick}
          className="flex items-center gap-3 touch-manipulation"
          aria-label={t('common.navigation.openMenu', 'Open navigation menu')}>
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white">
            <Box className="w-5 h-5" />
          </div>
          <div className="flex-1 overflow-hidden text-left">
            <div className="font-semibold text-sm truncate">{currentProject?.name || instanceInfo?.name || 'RITA'}</div>
            <div className="text-xs text-muted-foreground truncate">
              {instanceInfo?.cluster ? 'Cluster Mode' : 'Single Instance'}
            </div>
          </div>
        </button>

        <div className="flex items-center gap-2">
          {!isDashboard && <FilterTrigger />}
          <button className="p-2 rounded-md hover:bg-accent" aria-label={t('shipment.notifications', 'Notifications')}>
            <Bell className="w-5 h-5" />
          </button>
          <button className="p-2 rounded-md hover:bg-accent" aria-label={t('common.navigation.userMenu', 'User menu')}>
            <User className="w-5 h-5" />
          </button>
        </div>
      </div>

      {!isDashboard && <HeaderSearch />}
    </header>
  );
};
